import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

export type RoutingMode = 'parallel' | 'sequential'
export type RecordingNoticeMode = 'auto' | 'manual'

export interface CompanyDetail {
  id: number
  name: string
  timezone: string
  phone_number: string | null
  number_sid: string | null
  emergency_address_sid: string | null
  number_active: boolean
  admin_disabled: boolean
  has_active_number: boolean
  address1: string | null
  address2: string | null
  city: string | null
  province: string | null
  postal: string | null
  routing_mode: RoutingMode
  ring_timeout: number
  after_hours_message: string | null
  open_hours_message: string | null
  skip_welcome_message: boolean
  voice: string
  include_web_agents_parallel: boolean
  include_web_agents_sequential: boolean
  web_agents_first_sequential: boolean
  include_phone_agents: boolean
  max_agents: number
  phone_agent_whisper_message: string | null
  record_inbound_calls: boolean
  record_outbound_calls: boolean
  recording_notice_mode: RecordingNoticeMode
  recording_notice_message: string | null
  recordings_retention_days: number
  /** Appels entre collègues (Réglages ▸ Équipe) : aucun numéro ne les porte. */
  record_internal_calls: boolean
  internal_recordings_retention_days: number
  internal_recording_notice_mode: RecordingNoticeMode
  /** Read as written to both colleagues when the notice is automatic. */
  internal_recording_notice_message: string
  /** Empty: the main number's voice (shown by internal_recording_notice_voice_default). */
  internal_recording_notice_voice: string
  internal_recording_notice_voice_default: string
  recording_addon_active: boolean
  /** Enregistrement inclus (tout plan actuel, ou add-on legacy) */
  recording_allowed?: boolean
  /** Ce que le plan permet (verrous serveur) ; absent = tout ouvert. */
  plan_rights?: Partial<Record<PlanFeature, boolean>>
  toll_free_addon_active: boolean
  /** Add-on WhatsApp actif — gate l'inbox WhatsApp. Read-only (dérivé de CompanyAddon). */
  whatsapp_enabled: boolean
  transcription_enabled: boolean
  ai_summary_enabled: boolean
  preferred_phone_number: string | null
  port_request_number: string | null
  port_request_status: string | null
  is_toll_free: boolean
  company_size: string | null
  industry: string | null
  website: string | null
  is_configured: boolean
  is_trialing: boolean
  created_at: string
  updated_at: string
}

/**
 * Sous-ensemble éditable pour les patchs partiels Company.
 * Note : `voice`, `record_*`, `recording_notice_*`, `recordings_retention_days`,
 * `transcription_enabled`, `ai_summary_enabled` sont désormais portés par
 * chaque PhoneNumber et ne passent plus par cet endpoint (cf. Phase 4).
 */
export type CompanyUpdateInput = Partial<
  Pick<
    CompanyDetail,
    | 'name'
    | 'timezone'
    | 'address1'
    | 'address2'
    | 'city'
    | 'province'
    | 'postal'
    | 'routing_mode'
    | 'ring_timeout'
    | 'after_hours_message'
    | 'open_hours_message'
    | 'skip_welcome_message'
    | 'include_phone_agents'
    | 'phone_agent_whisper_message'
    | 'industry'
    | 'website'
    | 'company_size'
    | 'record_internal_calls'
    | 'internal_recordings_retention_days'
    | 'internal_recording_notice_mode'
    | 'internal_recording_notice_message'
    | 'internal_recording_notice_voice'
  >
>


export interface CompanyScheduleDays {
  Mon: { open: string; close: string }
  Tue: { open: string; close: string }
  Wed: { open: string; close: string }
  Thu: { open: string; close: string }
  Fri: { open: string; close: string }
  Sat: { open: string; close: string }
  Sun: { open: string; close: string }
}

export interface CompanySchedule {
  open_message: string
  closed_message: string
  days: CompanyScheduleDays
}

export type AssignmentType = 'main' | 'ai_agent' | 'unassigned'

export interface CompanyPhoneNumber {
  id: number
  phone_number: string
  label: string | null
  /** Carrier of the number: its voices, its browser phone. */
  fournisseur?: 'twilio' | 'telnyx'
  assignment_type: AssignmentType
  number_sid: string | null
  is_toll_free: boolean
  /** Sans-frais : textos permis seulement une fois l'entreprise vérifiée auprès des opérateurs. */
  tollfree_sms_status?: 'unknown' | 'none' | 'pending' | 'approved' | 'rejected'
  tollfree_sms_reason?: string
  country_code: string | null
  area_code: string | null
  purchased_at: string
  also_used_by_ai: boolean
  ai_agent_name: string | null
  ai_agent_id: number | null
  ivr_flow_id: number | null
  ivr_flow_name: string | null
  ivr_flow_is_active: boolean | null
  ivr_flow_node_count: number | null
  owner_name: string | null
  owner_type: 'company' | 'ai_agent' | null
  owner_id?: number
  // Configuration par-numéro (migrée depuis Company en Phase 1).
  voice: string
  record_inbound_calls: boolean
  record_outbound_calls: boolean
  recordings_retention_days: number
  recording_notice_mode: RecordingNoticeMode
  recording_notice_message: string | null
  transcription_enabled: boolean
  ai_summary_enabled: boolean
  // Adresse E911 par-numéro (migrée depuis Company en Phase E911).
  address1: string | null
  address2: string | null
  city: string | null
  province: string | null
  postal: string | null
  emergency_address_sid: string | null
  /** État réel chez Twilio, recopié par le serveur. */
  e911_statut?: '' | 'en_cours' | 'declaree' | 'a_corriger' | 'sans_objet'
  e911_erreur?: string
  /** Pays de l'adresse 911, déduit de l'indicatif du numéro. */
  pays_urgence?: 'CA' | 'US'
}

export interface CompanyPhoneNumberStats {
  total: number
  main: number
  ai_agent: number
  unassigned: number
}

/** Fonctions verrouillées par plan, mêmes clés que le backend (droits_plan.py). */
export type PlanFeature =
  | 'horaires_utilisateur'
  | 'numeros_partages'
  | 'crm_equipe'
  | 'collaboration_equipe'
  | 'power_dialer'
  | 'analytics_equipe'
  | 'appels_sortants_agent'

/** Plan minimum de chaque fonction. */
export const PLAN_FEATURE_MIN: Record<PlanFeature, 'team' | 'business'> = {
  horaires_utilisateur: 'team',
  numeros_partages: 'team',
  crm_equipe: 'team',
  collaboration_equipe: 'team',
  power_dialer: 'business',
  analytics_equipe: 'team',
  appels_sortants_agent: 'business',
}

interface CompanyPhoneNumbersResponse {
  phone_numbers: CompanyPhoneNumber[]
  stats: CompanyPhoneNumberStats
}

/** GET /api/company/{id}/ — détail entreprise. */
export function useCompany(companyId: number | undefined) {
  return useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => {
      const { data } = await api.get<CompanyDetail>(`/api/company/${companyId}/`)
      return data
    },
    enabled: !!companyId,
    staleTime: 60_000,
  })
}

/** PUT /api/company/{id}/ — patch partiel des champs entreprise. */
export function useUpdateCompany(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CompanyUpdateInput) => {
      const { data } = await api.put<CompanyDetail>(
        `/api/company/${companyId}/`,
        payload,
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['company', companyId], data)
    },
  })
}

/** GET /api/companies/{id}/phone-numbers/ — numéros + assignations + stats. */
export function useCompanyPhoneNumbers(companyId: number | undefined) {
  return useQuery({
    queryKey: ['company-phone-numbers', companyId],
    queryFn: async () => {
      const { data } = await api.get<CompanyPhoneNumbersResponse>(
        `/api/companies/${companyId}/phone-numbers/`,
      )
      return data
    },
    enabled: !!companyId,
    staleTime: 30_000,
  })
}

/** GET /api/companies/{id}/schedule/ — horaires d'ouverture entreprise. */
export function useCompanySchedule(companyId: number | undefined) {
  return useQuery({
    queryKey: ['company-schedule', companyId],
    queryFn: async () => {
      const { data } = await api.get<CompanySchedule>(
        `/api/companies/${companyId}/schedule/`,
      )
      return data
    },
    enabled: !!companyId,
  })
}

/** PUT /api/companies/{id}/schedule/ — sauvegarder les horaires. */
export function useUpdateCompanySchedule(companyId: number | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: CompanySchedule) => {
      const { data } = await api.put<{ message: string }>(
        `/api/companies/${companyId}/schedule/`,
        payload,
      )
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-schedule', companyId] })
    },
  })
}
